import {Injectable} from '@angular/core';
import {Action, createSelector, State, StateContext} from '@ngxs/store';
import {JsonApiResponse} from '@tisa-group/cdk';
import {LoadPageTemplate} from '@store/page-template/page-template.actions';
import {Observable, tap} from 'rxjs';
import {CurrentPageService} from '@tisa-group/core';
import {patch} from '@ngxs/store/operators';
import {UmpirePageTemplate} from '@tisa-group/models';
import {OttComponentsHelper} from '@core/utils/ott-components.helper';

export type PageTemplateStateModel = Record<string, JsonApiResponse<UmpirePageTemplate>>;

@State<PageTemplateStateModel>({
  name: 'pageTemplate',
  defaults: {},
})
@Injectable()
export class PageTemplateState {
  @Action(LoadPageTemplate)
  loadPageTemplate(
    ctx: StateContext<PageTemplateStateModel>,
    action: LoadPageTemplate,
  ):
    | Observable<JsonApiResponse<UmpirePageTemplate>>
    | JsonApiResponse<UmpirePageTemplate> {
    const pageTemplates = ctx.getState();
    const pageTemplateType = action.slug;

    if (pageTemplates[pageTemplateType]) {
      return pageTemplates[pageTemplateType];
    }

    return this.currentPageService
      .get(
        action.slug,
        [
          'components',
          // Hero thematic & Advert
          'components.image',
          // Hero main
          'components.events',
          'components.format',
          'components.events.format',
          'components.events.segment',
          'components.events.image',
          // Formats
          'components.formats',
          'components.formats.image_horizontal',
          'components.formats.image_vertical',
          // Format
          'components.events',
          'components.events.image_horizontal',
          'components.events.image_vertical',
          // Playlists
          'components.playlists',
          'components.playlists.image_horizontal',
          'components.playlists.image_vertical',
          // Playlist
          'components.playlist',
        ].join(','),
      )
      .pipe(
        tap(pageTemplateResult => {
          const croppedComponents = OttComponentsHelper.mapToCroppedComponents(
            pageTemplateResult.data.components,
          );

          ctx.setState(
            patch({
              [action.slug]: {
                ...pageTemplateResult,
                data: {
                  ...pageTemplateResult.data,
                  components: croppedComponents,
                },
              } as JsonApiResponse<UmpirePageTemplate>,
            }),
          );
        }),
      );
  }

  static getPageTemplateBySlug(
    slug: string,
  ): (state: PageTemplateStateModel) => JsonApiResponse<UmpirePageTemplate> {
    return createSelector<
      (state: PageTemplateStateModel) => JsonApiResponse<UmpirePageTemplate>
    >([PageTemplateState], state => {
      return state[slug];
    });
  }

  constructor(readonly currentPageService: CurrentPageService) {}
}
